import { Button, ConfigProvider, Flex, Input, Segmented, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { IoDownloadOutline } from 'react-icons/io5';
import WorklistTagFilter from './WorklistTagFilter';
import styles from './WorklistFilterRail.module.css';

const filterTheme = {
  components: {
    Input: { colorBgContainer: 'var(--sub-sidebar-surface-1)' },
    Select: { colorBgContainer: 'var(--sub-sidebar-surface-1)' },
  },
};

export type ReportStatusFilter = 'DRAFT' | 'PRELIMINARY' | 'FINAL';
export type PriorityFilter = 'STAT' | 'URGENT' | 'ROUTINE';

const REPORT_STATUS_OPTIONS: { value: ReportStatusFilter; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PRELIMINARY', label: 'Preliminary' },
  { value: 'FINAL', label: 'Finalized' },
];

const PRIORITY_OPTIONS: { value: PriorityFilter; label: string }[] = [
  { value: 'STAT', label: 'Stat' },
  { value: 'URGENT', label: 'Urgent' },
  { value: 'ROUTINE', label: 'Routine' },
];

export interface WorklistFilterRailProps {
  assignedToMe: boolean;
  onAssignedChange: (v: boolean) => void;
  reportStatusFilters: ReportStatusFilter[];
  onReportStatusChange: (v: ReportStatusFilter[]) => void;
  priorityFilters: PriorityFilter[];
  onPriorityChange: (v: PriorityFilter[]) => void;
  tagFilters: string[];
  onTagChange: (v: string[]) => void;
  tags?: string[];
  searchText: string;
  onSearchChange: (text: string) => void;
  onExportCsv: () => void;
}

export default function WorklistFilterRail({
  assignedToMe,
  onAssignedChange,
  reportStatusFilters,
  onReportStatusChange,
  priorityFilters,
  onPriorityChange,
  tagFilters,
  onTagChange,
  tags,
  searchText,
  onSearchChange,
  onExportCsv,
}: WorklistFilterRailProps) {
  return (
    <ConfigProvider theme={filterTheme}>
      <Flex vertical gap={12} className={styles.filterRail}>
        <Segmented
          block
          size="small"
          value={assignedToMe ? 'mine' : 'all'}
          onChange={(v) => onAssignedChange(v === 'mine')}
          options={[
            { value: 'mine', label: 'Assigned to me' },
            { value: 'all', label: 'All studies' },
          ]}
        />

        <div className={styles.filterGroup}>
          <label
            htmlFor="worklist-report-status"
            className={styles.filterLabel}
          >
            Report Status
          </label>
          <Select
            id="worklist-report-status"
            mode="multiple"
            size="small"
            placeholder="All statuses"
            value={reportStatusFilters}
            onChange={onReportStatusChange}
            options={REPORT_STATUS_OPTIONS}
            className={styles.fullWidth}
            popupMatchSelectWidth={false}
            maxTagCount="responsive"
            allowClear
          />
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="worklist-priority" className={styles.filterLabel}>
            Priority
          </label>
          <Select
            id="worklist-priority"
            mode="multiple"
            size="small"
            placeholder="All priorities"
            value={priorityFilters}
            onChange={onPriorityChange}
            options={PRIORITY_OPTIONS}
            className={styles.fullWidth}
            popupMatchSelectWidth={false}
            maxTagCount="responsive"
            allowClear
          />
        </div>

        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Tags</span>
          <WorklistTagFilter
            value={tagFilters}
            onChange={onTagChange}
            options={tags}
          />
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="worklist-search" className={styles.filterLabel}>
            Search
          </label>
          <Input
            id="worklist-search"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Patient name"
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        <Button icon={<IoDownloadOutline />} onClick={onExportCsv}>
          Export CSV
        </Button>
      </Flex>
    </ConfigProvider>
  );
}
